import { UUID, uuidv7 } from "uuidv7"

import { Property, PropertySchema, PropertyStatus } from "../entity"
import { InterfaceSelection } from "../entity/InterfaceSelection"

export class InterfaceSearch {
  _search: Search

  constructor(search: Search) {
    this._search = search
  }

  static from(search?: Search) {
    if (search)
      return new InterfaceSearch(search)
  }

  /**
   * NOTE: FileEvent is just an alias for File (for now) but this may change
   */
  static fromEvent(event: SearchEvent): InterfaceSearch {
    return new InterfaceSearch(event)
  }

  static clientPreHashId() {
    return `00050000-${uuidv7()}`
  }

  /** When we don't have enough info about a file to type it */
  static blank(searchId: string) {
    const selection = InterfaceSelection.nothing().inner()
    return new InterfaceSearch({ searchId, properties: {}, selection })
  }

  get properties() {
    return this._search.properties
  }

  get selection() {
    return new InterfaceSelection(this._search.selection)
  }

  inner(): Readonly<Search> {
    return this._search
  }

  isBlank() {
    return Object.keys(this._search.properties).length === 0
  }

  searchId() {
    return this._search.searchId
  }

  static async idFromPrehash(preHash: string) {
    const [apiId, keyId] = partition(preHash, "-")
    const hash = await window.crypto.subtle.digest("SHA-256", UUID.parse(keyId).bytes)
    const uuid = UUID.ofInner(new Uint8Array(hash.slice(0, 16)))
    return `${apiId}-${uuid.toString()}` as Property
  }

  addProperty(property: SearchProperty) {
    this._search.properties[property.propertyId] = property
  }

  removeProperty(property: SearchProperty) {
    delete this._search.properties[property.propertyId]
  }

  searchProperty(id: Property): SearchProperty | undefined {
    return this._search.properties?.[id]
  }

  executedAt(): number | undefined {
    const property = this._search.properties[Property.SEARCH_EXECUTED_AT]
    if (!property)
      return
    if (property.value.type !== PropertySchema.UtcTimestampMillisValue)
      throw new Error("Invalid search intent schema")
    return property.value.utcTimestampMillis
  }

  searchResults(): Record<string, SearchResult> {
    const results = Object.values(this._search.properties)
      .filter(property => property!.value.type === PropertySchema.SearchResultValue)
      .map((property) => {
        if (property!.value.type !== PropertySchema.SearchResultValue)
          throw new Error("Invalid search result schema")
        return property!.value.result
      })
    return dictByKey(results, result => result.fileId)
  }

  description() {
    const property = this._search.properties[Property.SEARCH_DESCRIPTION]
    if (!property)
      return
    if (property.value.type !== PropertySchema.NonEmptyStringValue)
      throw new Error("Invalid search description schema")
    return property.value.nonEmptyString
  }

  definition() {
    const property = this._search.properties[Property.SEARCH_DEFINITION]
    if (!property)
      return
    if (property.value.type !== PropertySchema.SearchDefinitionValue)
      throw new Error("Invalid search definition schema")
    return property.value.definition
  }

  /**
   * Merges two interface search's based on their properties. The "newer" one doesn't have
   * to be newer as such, but we take its properties and path by default, so it should
   * be the more recently retrieved search if possible.
   */
  mergeWithNewer(newer?: InterfaceSearch) {
    if (!newer)
      return this
    if (newer.searchId !== this.searchId)
      throw new Error("Cannot merge searches with different search ids!")
    for (const prop of Object.values(newer._search.properties)) {
      const thisProp = this._search.properties[prop!.propertyId]
      if (!prop)
        continue
      else if (!thisProp)
        this._search.properties[prop!.propertyId] = prop
      else if (thisProp && (thisProp.epochId ?? 0) < (prop.epochId ?? 0))
        this._search.properties[prop!.propertyId] = prop
      else if (thisProp && (thisProp.version ?? 0) < (prop.version ?? 0))
        this._search.properties[prop!.propertyId] = prop
      else if (thisProp && (thisProp.timestamp) < (prop.timestamp!))
        this._search.properties[prop!.propertyId] = prop
      else if (thisProp && thisProp.status !== PropertyStatus.StatusCurrent && prop.status === PropertyStatus.StatusCurrent)
        this._search.properties[prop!.propertyId] = prop
    }
    // Return a new Interface search with old inner because otherwise the reactivity hooks in vue
    // don't update (the hook is attached to the outermost class's reflection methods)
    return new InterfaceSearch(this._search)
  }
}
